import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { BugReport as BugIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Atualiza o state para que o próximo render mostre o fallback UI
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error,
      errorInfo
    });
    
    // Você pode registrar o erro em um serviço de relatório de erro aqui
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  public render(): ReactNode {
    if (this.state.hasError) {
      // Renderizar fallback UI personalizado ou o fornecido via props
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <Paper 
          elevation={3}
          sx={{
            p: 4,
            m: 2,
            maxWidth: '800px',
            mx: 'auto',
            mt: 8,
            textAlign: 'center'
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <BugIcon color="error" sx={{ fontSize: 60 }} />
            <Typography variant="h5" gutterBottom color="error">
              Algo deu errado ao renderizar este componente
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {this.state.error?.toString()}
            </Typography>
            
            <Box 
              sx={{ 
                bgcolor: 'background.default', 
                p: 2, 
                borderRadius: 1,
                width: '100%',
                mb: 3,
                overflow: 'auto',
                maxHeight: '200px',
                textAlign: 'left'
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {this.state.errorInfo?.componentStack}
              </pre>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={this.handleReset}
              >
                Tentar Novamente
              </Button>
              
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => window.location.href = '/'}
              >
                Voltar para Home
              </Button>
            </Box>
          </Box>
        </Paper>
      );
    }

    // Se não houver erro, renderizar os filhos normalmente
    return this.props.children;
  }
}

export default ErrorBoundary;